<template>
  <section class="custom-app-usage">
    <div class="row align-items-center">
      <div class="col">
        <h4 class="mb-1">ADAS Map Mobile usage &amp; credits</h4>
        <p class="text-muted mb-0">
          Everyone with ADAS Map Mobile access, from the moment it's turned on: their credits, what they've used and what is billed. Add credits,
          change the billing mode or state, or revoke access for anyone here; every change records who made it.
        </p>
      </div>
      <div class="col-auto">
        <router-link to="/app-pricing" class="btn btn-sm btn-outline-secondary me-2">App pricing</router-link>
        <router-link to="/access-requests" class="btn btn-sm btn-outline-secondary">Access requests</router-link>
      </div>
    </div>
    <p v-if="list.terms" class="text-muted small mt-2 mb-0" data-test="credit-terms">
      Subscription (the default): a plan of {{ credits(list.terms.planCredits) }} credits ({{ usd(list.terms.planPriceUsd) }}) is granted and billed
      on sign-up and on every billing anniversary, used or not. Unused plan credits expire then; top-off credits carry over. When a use needs more
      than the balance holds, a top-off of {{ credits(list.terms.topUpCredits) }} credits ({{ usd(list.terms.topUpPriceUsd) }}) is granted and billed
      automatically. Unlimited: no credits and no balance; every use is billed itself at {{ usd(list.terms.creditPriceUsd) }} a credit, after the
      fact.<span data-test="daily-max-terms">
        Daily maximums, each on its own and in both modes (users are never shown them): App usage
        {{ dailyMaxText(list.terms.dailyAppUsageMaxUsd) }}, ADAS IQ usage {{ dailyMaxText(list.terms.dailyAdasIqUsageMaxUsd) }}.</span
      >
      Nothing here marks a bill paid: billing is read from the monthly record under To bill. Prices are set on
      <router-link to="/app-pricing">App pricing</router-link>.
    </p>
    <div v-if="error" class="alert alert-danger mt-3 mb-0" role="alert">{{ error }}</div>

    <!-- One user -->
    <div v-if="selected" class="card mt-3" data-test="user-detail">
      <div class="card-header d-flex align-items-center">
        <div class="flex-grow-1">
          <span class="fw-semibold" data-test="user-detail-name">{{ selected.user.name || selected.user.username }}</span>
          <span class="text-muted ms-2">{{ selected.user.email || selected.user.username }}</span>
          <span v-if="!selected.user.enabled" class="badge bg-secondary ms-2">Access off</span>
          <span v-if="selected.user.isInactive" class="badge bg-secondary ms-2">Inactive</span>
          <span
            v-if="selected.credits.billingState && selected.credits.billingState !== 'active'"
            class="badge ms-2"
            :class="stateBadge(selected.credits.billingState)"
          >
            {{ stateName(selected.credits.billingState) }}
          </span>
          <div class="text-muted small">
            {{ selected.user.firmName
            }}<span v-if="selected.user.corporateFirmName && selected.user.corporateFirmName !== selected.user.firmName">
              · {{ selected.user.corporateFirmName }}</span
            >
          </div>
        </div>
        <button class="btn btn-sm btn-link" type="button" @click="closeUser">Close</button>
      </div>
      <div class="card-body">
        <div v-if="saved" class="alert alert-success" role="status">{{ saved }}</div>

        <!-- Billing cycle, what is billed, and what the app will do right now -->
        <div class="mb-3">
          <div data-test="billing-cycle">
            <span class="fw-semibold">{{ cycleText }}</span>
            <span v-if="isSubscription && selected.credits.cyclesBehind > 0" class="badge bg-warning text-dark ms-2" data-test="cycles-behind">
              {{ plural(selected.credits.cyclesBehind, 'renewal') }} behind
            </span>
          </div>
          <div class="small mt-1" data-test="billed-this-month">
            Billed this month: <strong>{{ usd(selected.credits.billedThisMonthUsd) }}</strong>
          </div>
          <div class="small" data-test="billing-daily-max-app">
            App usage today: {{ usd(selected.credits.appUsedTodayUsd || 0) }}
            {{ selected.credits.dailyAppUsageMaxUsd > 0 ? `of the ${usd(selected.credits.dailyAppUsageMaxUsd)} daily maximum.` : '(no limit).' }}
            <span v-if="selected.credits.appDailyMaxReached" class="badge bg-warning text-dark">Reached: app functions refused</span>
          </div>
          <div class="small" data-test="billing-daily-max-adas-iq">
            ADAS IQ usage today: {{ usd(selected.credits.adasIqUsedTodayUsd || 0) }}
            {{
              selected.credits.dailyAdasIqUsageMaxUsd > 0 ? `of the ${usd(selected.credits.dailyAdasIqUsageMaxUsd)} daily maximum.` : '(no limit).'
            }}
            <span v-if="selected.credits.adasIqDailyMaxReached" class="badge bg-warning text-dark">Reached: ADAS IQ refused</span>
          </div>
          <!-- The daily maximums say what they refuse just above; this line is the billing state's. -->
          <div v-if="selected.credits.refusal && selected.credits.refusal !== 'daily_max'" class="text-danger small mt-1" data-test="refusal">
            Uses are refused right now: {{ refusalName(selected.credits.refusal) }}.
          </div>
        </div>

        <!-- Credits -->
        <div class="row g-3">
          <template v-if="isSubscription">
            <div class="col-md-3">
              <div class="custom-tile" data-test="credits-left">
                <div class="custom-tile-value">{{ credits(creditsLeft) }}</div>
                <div class="text-muted small">credits left</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="custom-tile" data-test="plan-credits">
                <div class="custom-tile-value">{{ credits(selected.credits.planRemaining) }}</div>
                <div class="text-muted small">
                  plan credits (expire at renewal)<span v-if="selected.credits.pendingStart"> (the plan starts on the first use)</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="custom-tile" data-test="top-up-credits">
                <div class="custom-tile-value">{{ credits(selected.credits.topUpRemaining) }}</div>
                <div class="text-muted small">top-off credits (carry over)</div>
              </div>
            </div>
          </template>
          <div class="col-md-3">
            <div class="custom-tile" data-test="credits-used">
              <div class="custom-tile-value">{{ credits(selected.credits.usedThisMonth) }}</div>
              <div class="text-muted small">
                used this month<span v-if="isSubscription"> ({{ credits(selected.credits.usedThisCycle) }} this cycle)</span
                ><span v-else> ({{ usd(selected.credits.usedThisMonthUsd) }})</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="row g-3 mt-1">
          <div class="col-lg-7">
            <form class="custom-panel" data-test="add-credits" @submit.prevent="submitCredits">
              <div class="fw-semibold mb-2">Add credits</div>
              <div class="mb-2">
                <div v-for="option of creditKinds" :key="option.kind" class="form-check form-check-inline">
                  <input
                    :id="`kind-${option.kind}`"
                    v-model="creditDraft.kind"
                    class="form-check-input"
                    type="radio"
                    name="credit-kind"
                    :value="option.kind"
                  />
                  <label class="form-check-label" :for="`kind-${option.kind}`">{{ option.label }}</label>
                </div>
              </div>
              <div class="row g-2 align-items-end">
                <div class="col-auto">
                  <template v-if="creditDraft.kind === 'top-up'">
                    <label class="form-label small mb-1" for="credit-count">Top-offs</label>
                    <input
                      id="credit-count"
                      v-model.number="creditDraft.count"
                      type="number"
                      class="form-control form-control-sm"
                      min="1"
                      max="20"
                      step="1"
                      required
                    />
                  </template>
                  <template v-else>
                    <label class="form-label small mb-1" for="credit-amount">Credits</label>
                    <input
                      id="credit-amount"
                      v-model.number="creditDraft.credits"
                      type="number"
                      class="form-control form-control-sm"
                      :min="creditDraft.kind === 'grant' ? 0.01 : -10000"
                      max="10000"
                      step="0.01"
                      required
                    />
                  </template>
                </div>
                <div class="col">
                  <label class="form-label small mb-1" for="credit-note">{{ creditDraft.kind === 'top-up' ? 'Note (optional)' : 'Reason' }}</label>
                  <input
                    id="credit-note"
                    v-model="creditDraft.note"
                    type="text"
                    class="form-control form-control-sm"
                    maxlength="1000"
                    :required="creditDraft.kind !== 'top-up'"
                  />
                </div>
                <div class="col-auto">
                  <button class="btn btn-sm btn-primary" type="submit" :disabled="loading.saving">{{ creditButton }}</button>
                </div>
              </div>
              <div class="text-muted small mt-1">{{ creditHelp }}</div>
            </form>
          </div>
          <div class="col-lg-5">
            <form class="custom-panel" data-test="billing-state" @submit.prevent="submitBillingState">
              <div class="d-flex align-items-baseline mb-2">
                <span class="fw-semibold">Billing state</span>
                <span class="badge ms-2" :class="stateBadge(selected.credits.billingState)" data-test="billing-state-badge">{{
                  stateName(selected.credits.billingState)
                }}</span>
              </div>
              <div v-if="selected.credits.billingStateSetOn" class="text-muted small mb-2" data-test="billing-state-history">
                Set {{ formatDate(selected.credits.billingStateSetOn)
                }}<span v-if="selected.credits.billingStateSetByName"> by {{ selected.credits.billingStateSetByName }}</span
                ><span v-if="selected.credits.billingStateNote">: {{ selected.credits.billingStateNote }}</span>
              </div>
              <div class="row g-2 align-items-end">
                <div class="col-auto">
                  <label class="form-label small mb-1" for="billing-state-select">State</label>
                  <select id="billing-state-select" v-model="stateDraft.state" class="form-select form-select-sm">
                    <option value="active">Active</option>
                    <option value="hold">On hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div class="col">
                  <label class="form-label small mb-1" for="billing-state-note">{{
                    stateDraft.state === 'active' ? 'Note (optional)' : 'Reason'
                  }}</label>
                  <input
                    id="billing-state-note"
                    v-model="stateDraft.note"
                    type="text"
                    class="form-control form-control-sm"
                    maxlength="500"
                    :required="stateDraft.state !== 'active'"
                  />
                </div>
                <div class="col-auto">
                  <button class="btn btn-sm btn-outline-primary" type="submit" :disabled="loading.saving">Save state</button>
                </div>
              </div>
              <ul class="text-muted small ps-3 mt-2 mb-0">
                <li><strong>Active</strong>: normal service.</li>
                <li><strong>On hold</strong>: a declined payment. No charged use, no top-off, no renewal; the credits on hand are frozen.</li>
                <li>
                  <strong>Cancelled</strong>: a subscription spends what it has until its cycle ends and is not renewed; unlimited stops at once.
                </li>
                <li>In every state the user can still sign in and see their history.</li>
              </ul>
            </form>
          </div>
        </div>

        <!-- Billing mode and access (ADR-0016) -->
        <div class="row g-3 mt-1">
          <div class="col-lg-7">
            <form class="custom-panel" data-test="billing-mode" @submit.prevent="submitBillingMode">
              <div class="d-flex align-items-baseline mb-2">
                <span class="fw-semibold">Billing</span>
                <span class="badge ms-2" :class="modeBadge(selected.credits.billingMode)" data-test="billing-mode-badge">{{
                  modeName(selected.credits.billingMode)
                }}</span>
                <span v-if="selected.credits.billingModeSetOn" class="text-muted small ms-2">
                  set {{ formatDate(selected.credits.billingModeSetOn)
                  }}<span v-if="selected.credits.billingModeSetByName"> by {{ selected.credits.billingModeSetByName }}</span>
                </span>
              </div>
              <div v-if="selected.credits.billingModeNote" class="text-muted small mb-2">{{ selected.credits.billingModeNote }}</div>
              <div class="row g-2 align-items-end">
                <div class="col-auto">
                  <label class="form-label small mb-1" for="billing-mode-select">Mode</label>
                  <select id="billing-mode-select" v-model="billingDraft.mode" class="form-select form-select-sm">
                    <option value="subscription">Subscription</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                </div>
                <div class="col">
                  <label class="form-label small mb-1" for="billing-note">Note</label>
                  <input id="billing-note" v-model="billingDraft.note" type="text" class="form-control form-control-sm" maxlength="500" />
                </div>
                <div class="col-auto">
                  <button class="btn btn-sm btn-outline-primary" type="submit" :disabled="loading.saving">Save billing</button>
                </div>
              </div>
              <div class="text-muted small mt-1">{{ billingHelp }}</div>
            </form>
          </div>
          <div class="col-lg-5">
            <form class="custom-panel" data-test="access" @submit.prevent="submitAccess">
              <div class="d-flex align-items-baseline mb-2">
                <span class="fw-semibold">Access</span>
                <span class="badge ms-2" :class="selected.user.enabled ? 'bg-success' : 'bg-secondary'" data-test="access-badge">{{
                  selected.user.enabled ? 'On' : 'Off'
                }}</span>
              </div>
              <div v-if="selected.credits.accessChangedOn" class="text-muted small mb-2" data-test="access-history">
                Changed {{ formatDate(selected.credits.accessChangedOn)
                }}<span v-if="selected.credits.accessChangedByName"> by {{ selected.credits.accessChangedByName }}</span
                ><span v-if="selected.credits.accessNote">: {{ selected.credits.accessNote }}</span>
              </div>
              <div class="row g-2 align-items-end">
                <div class="col">
                  <label class="form-label small mb-1" for="access-reason">{{ selected.user.enabled ? 'Reason' : 'Note (optional)' }}</label>
                  <input
                    id="access-reason"
                    v-model="accessDraft.note"
                    type="text"
                    class="form-control form-control-sm"
                    maxlength="500"
                    data-test="access-reason"
                    :required="selected.user.enabled"
                  />
                </div>
                <div class="col-auto">
                  <button
                    class="btn btn-sm"
                    :class="selected.user.enabled ? 'btn-outline-danger' : 'btn-outline-success'"
                    type="submit"
                    :disabled="loading.saving"
                    data-test="access-toggle"
                  >
                    {{ selected.user.enabled ? 'Revoke access' : 'Restore access' }}
                  </button>
                </div>
              </div>
              <div class="text-muted small mt-1">
                The hard stop: with access off the app refuses the user entirely and no renewal happens. Turned on again, a new billed cycle starts.
              </div>
            </form>
          </div>
        </div>
        <div v-if="draftError" class="text-danger small mt-2" role="alert">{{ draftError }}</div>

        <!-- Usage -->
        <h6 class="mt-4">Usage</h6>
        <table class="table table-bordered table-sm" data-test="user-usage">
          <thead>
            <tr>
              <th>Function</th>
              <th class="custom-number">This month</th>
              <th class="custom-number">This month, by ADAS IQ</th>
              <th class="custom-number">All time</th>
              <th>Last used</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ADAS IQ questions</td>
              <td class="custom-number">{{ selected.adasIq.questionsThisMonth }}</td>
              <td class="custom-number">–</td>
              <td class="custom-number">{{ selected.adasIq.questionsAllTime }}</td>
              <td>{{ formatDate(selected.adasIq.lastAsked) }}</td>
            </tr>
            <tr v-for="row of selected.usage" :key="row.function" :data-test="`usage-${row.function}`">
              <td>{{ functionName(row.function) }}</td>
              <td class="custom-number">{{ row.thisMonth }}</td>
              <td class="custom-number">{{ row.function === 'image-inspection' ? '–' : row.thisMonthViaAi }}</td>
              <td class="custom-number">{{ row.allTime + row.allTimeViaAi }}</td>
              <td>{{ formatDate(row.lastUsed) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Purchases -->
        <h6 class="mt-4">Credits added</h6>
        <table class="table table-bordered table-sm" data-test="user-purchases">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th class="custom-number">Credits</th>
              <th class="custom-number">Billed</th>
              <th>By</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!selected.purchases.length">
              <td colspan="6" class="text-muted">Nothing yet. A subscription's plan is added when it starts.</td>
            </tr>
            <tr v-for="(row, i) of selected.purchases" :key="i" :data-test="`purchase-${row.kind}`">
              <td>{{ formatDate(row.occurredOn) }}</td>
              <td>
                {{ purchaseKind(row.kind) }}
                <span v-if="row.channel === 'auto'" class="text-muted small">(automatic top-off)</span>
                <span v-else-if="row.channel === 'renewal'" class="text-muted small">(renewal)</span>
              </td>
              <td class="custom-number">{{ credits(row.credits) }}</td>
              <td class="custom-number">{{ row.billable && row.priceUsd > 0 ? usd(row.priceUsd) : '–' }}</td>
              <td>{{ row.recordedByName || (row.channel === 'auto' || row.channel === 'renewal' ? 'Automatic' : '') }}</td>
              <td>{{ row.note }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Charges -->
        <h6 class="mt-4">Charges <span class="text-muted small">(latest 100)</span></h6>
        <table class="table table-bordered table-sm" data-test="user-charges">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>VIN</th>
              <th>Charge</th>
              <th class="custom-number">Credits</th>
              <th class="custom-number">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!selected.charges.length">
              <td colspan="6" class="text-muted">No charges yet.</td>
            </tr>
            <tr v-for="(row, i) of selected.charges" :key="i">
              <td>{{ formatDate(row.chargedOn) }}</td>
              <td>
                {{ row.kind === 'ai-turn' ? 'ADAS IQ question' : functionName(row.functionCode) }}
                <span v-if="row.kind === 'function-use' && row.channel === 'adas-iq'" class="text-muted small">(by ADAS IQ)</span>
                <span v-if="row.usageBilled" class="text-muted small">(billed on usage)</span>
              </td>
              <td>{{ row.vin }}</td>
              <td>{{ reasonName(row.reason) }}</td>
              <td class="custom-number">{{ credits(row.credits) }}</td>
              <td class="custom-number">{{ row.amountUsd == null ? '' : usd(row.amountUsd) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- To bill (ADR-0016): what the monthly billing record holds for a month -->
    <div class="card mt-3" data-test="billing">
      <div class="card-header d-flex flex-wrap align-items-center gap-2">
        <span class="fw-semibold flex-grow-1">
          To bill
          <template v-if="billing">
            <strong class="ms-2" data-test="billing-total">{{ usd(billing.totalUsd) }}</strong>
            <span class="text-muted small ms-1"
              >for {{ monthName(billing.period) }}<span v-if="billing.asOf">, as of {{ formatDate(billing.asOf) }}</span></span
            >
          </template>
        </span>
        <input
          v-model="billingPeriod"
          type="month"
          class="form-control form-control-sm w-auto"
          aria-label="Month to bill"
          data-test="billing-period"
          @change="loadBilling"
        />
        <div class="btn-group btn-group-sm" role="group" aria-label="Group to bill by" data-test="billing-group">
          <button
            type="button"
            class="btn"
            :class="billingGroupBy === 'firm' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="groupBilling('firm')"
          >
            By firm
          </button>
          <button
            type="button"
            class="btn"
            :class="billingGroupBy === 'user' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="groupBilling('user')"
          >
            By user
          </button>
        </div>
        <div class="form-check mb-0">
          <input
            id="billing-include-test"
            v-model="billingIncludeTest"
            class="form-check-input"
            type="checkbox"
            data-test="billing-include-test"
            @change="loadBilling"
          />
          <label class="form-check-label small" for="billing-include-test">Include test users</label>
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading.billing" @click="downloadBillingCsv">CSV</button>
      </div>
      <div class="card-body p-0">
        <table class="table table-sm mb-0" data-test="billing-rows">
          <thead>
            <tr>
              <th>Firm</th>
              <th>NetSuite id</th>
              <template v-if="billingShownBy === 'user'">
                <th>User</th>
                <th>Mode</th>
                <th>State</th>
              </template>
              <th v-else class="custom-number">Users</th>
              <th class="custom-number">Plans</th>
              <th class="custom-number">Top-offs</th>
              <th class="custom-number">Admin top-ups</th>
              <th v-if="billingHasOther" class="custom-number">Other</th>
              <th class="custom-number">Usage</th>
              <th class="custom-number">Total</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading.billing">
              <td :colspan="billingColumns" class="text-muted">Loading…</td>
            </tr>
            <tr v-else-if="!billing || !billing.rows.length">
              <td :colspan="billingColumns" class="text-muted">Nothing to bill for {{ monthName(billing ? billing.period : billingPeriod) }}.</td>
            </tr>
            <tr
              v-for="(row, i) of (billing && billing.rows) || []"
              :key="i"
              :class="{ 'custom-row': row.user }"
              @click="row.user && openUser(row.user)"
            >
              <td>
                {{ row.firmName }}
                <div v-if="row.corporateFirmName && row.corporateFirmName !== row.firmName" class="text-muted small">{{ row.corporateFirmName }}</div>
              </td>
              <td>
                <template v-if="row.firmNetsuiteId">{{ row.firmNetsuiteId }}</template>
                <template v-else-if="row.corporateNetsuiteId"
                  >{{ row.corporateNetsuiteId }} <span class="text-muted small">(corporate)</span></template
                >
              </td>
              <template v-if="billingShownBy === 'user'">
                <td>
                  {{ row.name || row.username }}
                  <div class="text-muted small">{{ row.email || row.username }}</div>
                </td>
                <td>{{ modeName(row.billingMode) }}</td>
                <td>
                  <span class="badge" :class="stateBadge(row.billingState)">{{ stateName(row.billingState) }}</span>
                  <span v-if="row.accessEnabled === false" class="badge bg-secondary ms-1">Access off</span>
                </td>
              </template>
              <td v-else class="custom-number">{{ row.users }}</td>
              <td class="custom-number">{{ usd(row.planUsd) }}</td>
              <td class="custom-number">{{ usd(row.topOffUsd) }}</td>
              <td class="custom-number">{{ usd(row.adminTopUpUsd) }}</td>
              <td v-if="billingHasOther" class="custom-number">{{ usd(row.otherUsd) }}</td>
              <td class="custom-number">
                {{ usd(row.usageUsd) }}
                <div class="text-muted small">{{ credits(row.usageCredits) }} credits</div>
              </td>
              <td class="custom-number fw-semibold">{{ usd(row.totalUsd) }}</td>
              <td>{{ row.lastUsedOn ? formatDay(row.lastUsedOn) : '' }}</td>
              <td>
                <span v-if="row.idleCycles > 0" class="badge bg-secondary me-1">idle {{ plural(row.idleCycles, 'cycle') }}</span>
                <span v-if="row.cyclesBehind > 0" class="badge bg-warning text-dark">{{ row.cyclesBehind }} behind</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Everyone -->
    <form class="row g-2 align-items-end mt-3" @submit.prevent="search(1)">
      <div class="col-md-5">
        <label class="form-label small mb-1" for="usage-search">Find a user</label>
        <input
          id="usage-search"
          v-model="searchText"
          type="search"
          class="form-control form-control-sm"
          placeholder="Name, username, email or location"
          data-test="usage-search"
        />
      </div>
      <div class="col-auto">
        <button class="btn btn-sm btn-primary" type="submit">Search</button>
      </div>
      <div class="col text-end text-muted small">
        {{ list.total }} user{{ list.total === 1 ? '' : 's' }}<span v-if="list.period"> · {{ monthName(list.period) }}</span>
      </div>
    </form>

    <div v-show="loading.list && !list.users.length" class="custom-spinner">
      <FontAwesomeIcon icon="fa-solid fa-spinner" spin />
    </div>
    <table class="table table-bordered table-hover align-middle mt-2" data-test="usage-users">
      <thead>
        <tr>
          <th>User</th>
          <th>Location</th>
          <th>Billing</th>
          <th class="custom-number">Credits left</th>
          <th class="custom-number">Used</th>
          <th class="custom-number">Billed this month</th>
          <th class="custom-number">ADAS IQ</th>
          <th class="custom-number">History</th>
          <th class="custom-number">Identify</th>
          <th class="custom-number">Images</th>
          <th>Last used</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!loading.list && !list.users.length">
          <td colspan="11" class="text-muted">No one matches.</td>
        </tr>
        <tr
          v-for="row of list.users"
          :key="row.user"
          :data-test="`usage-user-${row.user}`"
          :class="{ 'table-active': selected && selected.user.user === row.user }"
          class="custom-row"
          @click="openUser(row.user)"
        >
          <td>
            <div class="fw-semibold">{{ row.name || row.username }}</div>
            <div class="text-muted small">{{ row.email || row.username }}</div>
            <span v-if="!row.enabled" class="badge bg-secondary">Access off</span>
            <span v-if="row.isInactive" class="badge bg-secondary ms-1">Inactive</span>
          </td>
          <td>
            <div>{{ row.firmName }}</div>
            <div v-if="row.corporateFirmName && row.corporateFirmName !== row.firmName" class="text-muted small">{{ row.corporateFirmName }}</div>
          </td>
          <td>
            <span class="badge" :class="modeBadge(row.billingMode)">{{ modeName(row.billingMode) }}</span>
            <span class="badge ms-1" :class="stateBadge(row.billingState)" data-test="row-billing-state">{{ stateName(row.billingState) }}</span>
          </td>
          <td class="custom-number" data-test="row-credits-left">
            <template v-if="row.billingMode !== 'unlimited'">{{ credits(rowCreditsLeft(row)) }}</template>
            <div v-if="row.billingMode !== 'unlimited' && row.pendingStart" class="text-muted small">starts on first use</div>
            <div v-if="row.billingMode !== 'unlimited' && row.cyclesBehind > 0" class="text-warning small">{{ row.cyclesBehind }} behind</div>
          </td>
          <td class="custom-number">{{ credits(row.usedThisMonth) }}</td>
          <td class="custom-number" data-test="row-billed-this-month">{{ usd(row.billedThisMonthUsd) }}</td>
          <td class="custom-number">{{ row.adasIqQuestions }}</td>
          <td class="custom-number">{{ row.historySearches }}</td>
          <td class="custom-number">{{ row.identifyLookups }}</td>
          <td class="custom-number">{{ row.imageInspections }}</td>
          <td>{{ row.lastUsed ? formatDate(row.lastUsed) : 'Not yet' }}</td>
        </tr>
      </tbody>
    </table>
    <p class="text-muted small">
      Counts and billed amounts are this month's (UTC). Credits left = plan credits plus top-off credits on hand now; an unlimited account has none.
      ADAS IQ = questions answered; History and Identify include lookups ADAS IQ made for the user.
    </p>
    <nav v-if="pages > 1" aria-label="User pages">
      <ul class="pagination pagination-sm">
        <li class="page-item" :class="{ disabled: list.page <= 1 }">
          <button class="page-link" type="button" @click="search(list.page - 1)">Previous</button>
        </li>
        <li class="page-item disabled">
          <span class="page-link">Page {{ list.page }} of {{ pages }}</span>
        </li>
        <li class="page-item" :class="{ disabled: list.page >= pages }">
          <button class="page-link" type="button" @click="search(list.page + 1)">Next</button>
        </li>
      </ul>
    </nav>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { appUsageStore } from '@/stores/appUsage'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'

const FUNCTIONS = {
  'history-search': 'ADAS Service History Search',
  'identify-adas': 'Identify ADAS Systems',
  'image-inspection': 'Images Based ADAS Identification'
}

const REASONS = {
  charged: 'Charged',
  'free-own-data': "Free: the user's own data",
  'free-relookup': 'Free: repeat lookup',
  'free-empty': 'Free: nothing found',
  'free-ai-lookup': 'Free: billed as AI usage',
  'free-unpriced': 'Free: no price set'
}

const PURCHASES = {
  plan: 'Plan',
  'top-up': 'Top-off',
  grant: 'Free credits',
  adjustment: 'Correction'
}

// ADR-0016: two billing modes, and a billing state that is the soft stop.
const MODES = { subscription: 'Subscription', unlimited: 'Unlimited' }
const MODE_BADGES = { subscription: 'bg-secondary', unlimited: 'bg-primary' }
const STATES = { active: 'Active', hold: 'On hold', cancelled: 'Cancelled' }
const STATE_BADGES = { active: 'bg-success', hold: 'bg-warning text-dark', cancelled: 'bg-danger' }
const REFUSALS = {
  billing_hold: 'the account is on billing hold',
  billing_cancelled: 'the account is cancelled',
  daily_max: 'the daily maximum ADAS IQ usage is reached'
}

export default {
  name: 'AppUsage',
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      searchText: '',
      error: null,
      saved: null,
      draftError: null,
      creditKinds: [
        { kind: 'grant', label: 'Free credits' },
        { kind: 'top-up', label: 'Top-off (billed)' },
        { kind: 'adjustment', label: 'Correction' }
      ],
      creditDraft: { kind: 'grant', count: 1, credits: null, note: '' },
      billingDraft: { mode: 'subscription', note: '' },
      stateDraft: { state: 'active', note: '' },
      accessDraft: { note: '' },
      // To bill: the current month (UTC) until the admin picks another.
      billingPeriod: new Date().toISOString().slice(0, 7),
      billingGroupBy: 'firm',
      billingIncludeTest: false
    }
  },
  computed: {
    ...mapState(appUsageStore, ['list', 'loading', 'billing']),
    ...mapState(appUsageStore, { selected: 'user' }),
    isSubscription() {
      return this.selected?.credits?.billingMode !== 'unlimited'
    },
    // Plan credits and top-off credits on hand now.
    creditsLeft() {
      const c = this.selected?.credits || {}
      return (c.planRemaining || 0) + (c.topUpRemaining || 0)
    },
    cycleText() {
      const c = this.selected?.credits
      if (!c) return ''
      if (c.billingMode === 'unlimited') {
        return `Billed on usage: ${this.credits(c.usedThisMonth) || 0} credits (${this.usd(c.usedThisMonthUsd) || '$0.00'}) this month`
      }
      if (c.pendingStart) return 'Plan starts on the first use'
      return `Started ${this.formatDay(c.billingStart)}, cycle ${c.cycleNumber}, renews ${this.formatDay(c.nextRenewalOn)}`
    },
    billingHelp() {
      return this.billingDraft.mode === 'unlimited'
        ? 'No credits and no balance. Every use is billed itself at the credit price, after the fact.'
        : 'The plan is granted and billed on sign-up and on every billing anniversary, used or not; unused plan credits expire then. When a use needs more than the balance holds, a top-off is granted and billed automatically, and top-off credits carry over. Nothing is asked of the user.'
    },
    // The columns follow the rows on screen, not the button, while a new grouping loads.
    billingShownBy() {
      return (this.billing && this.billing.groupBy) || this.billingGroupBy
    },
    billingHasOther() {
      return !!(this.billing && this.billing.rows.some((row) => row.otherUsd))
    },
    billingColumns() {
      return 9 + (this.billingShownBy === 'user' ? 3 : 1) + (this.billingHasOther ? 1 : 0)
    },
    pages() {
      return Math.max(1, Math.ceil(this.list.total / (this.list.pageSize || 50)))
    },
    creditButton() {
      return { grant: 'Add free credits', 'top-up': 'Record top-off', adjustment: 'Save correction' }[this.creditDraft.kind]
    },
    creditHelp() {
      const terms = this.list.terms
      if (this.creditDraft.kind === 'top-up') {
        return terms
          ? `Each top-off is ${this.credits(terms.topUpCredits)} credits, billed ${this.usd(
              terms.topUpPriceUsd
            )}. Top-off credits carry over from cycle to cycle.`
          : 'Billed at the current top-off price. Top-off credits carry over from cycle to cycle.'
      }
      if (this.creditDraft.kind === 'grant') {
        return 'Not billed. Added credits carry over from cycle to cycle.'
      }
      return 'Not billed. A negative number takes credits away.'
    }
  },
  async mounted() {
    await this.search(1)
    const fromLink = Number(this.$route.query.user)
    if (fromLink > 0) {
      await this.openUser(fromLink)
    }
    await this.loadBilling()
  },
  unmounted() {
    this.clearUser()
  },
  methods: {
    ...mapActions(appUsageStore, [
      'getUsers',
      'getUser',
      'clearUser',
      'addCredits',
      'setBillingMode',
      'setBillingState',
      'setAccess',
      'getBilling',
      'getBillingCsv'
    ]),
    async search(page) {
      this.error = null
      try {
        await this.getUsers(this.searchText.trim(), page)
      } catch (error) {
        this.error = `Could not load users: ${error}`
      }
    },
    async openUser(launchpadUser) {
      this.error = null
      this.saved = null
      this.draftError = null
      try {
        // Another row or Close came first: leave the panel, drafts and URL to that one.
        if (!(await this.getUser(launchpadUser))) return
        this.resetDrafts()
        if (Number(this.$route.query.user) !== launchpadUser) {
          this.$router.replace({ query: { ...this.$route.query, user: launchpadUser } })
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        this.error = `Could not load that user: ${error}`
      }
    },
    // The panel still shows this account and isn't switching to another (a save's answer is
    // only applied then).
    showing(launchpadUser) {
      return !this.loading.user && this.selected?.user?.user === launchpadUser
    },
    // A failed save's error goes on the panel only while it shows that account; otherwise it
    // goes in the banner, named, so it never lands on another account (QA698).
    saveFailed(launchpadUser, who, error) {
      if (this.showing(launchpadUser)) this.draftError = String(error)
      else this.error = `Couldn't save for ${who}: ${error}`
    },
    closeUser() {
      this.clearUser()
      this.saved = null
      const query = { ...this.$route.query }
      delete query.user
      this.$router.replace({ query })
    },
    resetDrafts() {
      this.creditDraft = { kind: 'grant', count: 1, credits: null, note: '' }
      const c = this.selected?.credits || {}
      this.billingDraft = { mode: c.billingMode === 'unlimited' ? 'unlimited' : 'subscription', note: '' }
      this.stateDraft = { state: c.billingState || 'active', note: '' }
      this.accessDraft = { note: '' }
    },
    // A save changes what is billed: refresh the list and the To bill table too.
    async refreshAfterSave() {
      await Promise.all([this.search(this.list.page), this.loadBilling()])
    },
    async submitBillingMode() {
      this.saved = null
      this.draftError = null
      const d = this.billingDraft
      const id = this.selected.user.user
      const who = this.selected.user.name || this.selected.user.username
      try {
        await this.setBillingMode(id, { billingMode: d.mode, note: d.note })
        if (this.showing(id)) {
          this.saved = `Billing set to ${this.modeName(d.mode)}.`
          this.resetDrafts()
        }
        await this.refreshAfterSave()
      } catch (error) {
        this.saveFailed(id, who, error)
      }
    },
    async submitBillingState() {
      this.saved = null
      this.draftError = null
      const d = this.stateDraft
      const note = String(d.note ?? '').trim()
      if (d.state !== 'active' && !note) {
        this.draftError = `Say why the account is ${d.state === 'hold' ? 'on hold' : 'cancelled'}; the reason stays with the billing state.`
        return
      }
      const id = this.selected.user.user
      const who = this.selected.user.name || this.selected.user.username
      try {
        await this.setBillingState(id, { billingState: d.state, note })
        if (this.showing(id)) {
          this.saved = `Billing state set to ${this.stateName(d.state).toLowerCase()}.`
          this.resetDrafts()
        }
        await this.refreshAfterSave()
      } catch (error) {
        this.saveFailed(id, who, error)
      }
    },
    async submitAccess() {
      this.saved = null
      this.draftError = null
      const enabled = !this.selected.user.enabled
      const note = String(this.accessDraft.note ?? '').trim()
      if (!enabled && !note) {
        this.draftError = 'Say why access is being revoked; the reason stays with the account.'
        return
      }
      const who = this.selected.user.name || this.selected.user.username
      if (!enabled && !window.confirm(`Revoke ADAS Map Mobile access for ${who}? The app will refuse them entirely and no renewal will happen.`)) {
        return
      }
      const id = this.selected.user.user
      try {
        await this.setAccess(id, { enabled, note })
        if (this.showing(id)) {
          this.saved = enabled ? 'Access restored.' : 'Access revoked.'
          this.resetDrafts()
        }
        await this.refreshAfterSave()
      } catch (error) {
        this.saveFailed(id, who, error)
      }
    },
    groupBilling(groupBy) {
      this.billingGroupBy = groupBy
      return this.loadBilling()
    },
    async loadBilling() {
      // A cleared or half-typed month picker has nothing to ask for yet.
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(this.billingPeriod)) return
      try {
        await this.getBilling(this.billingPeriod, this.billingGroupBy, this.billingIncludeTest)
      } catch (error) {
        this.error = `Could not load what is to bill: ${error}`
      }
    },
    async downloadBillingCsv() {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(this.billingPeriod)) return
      try {
        const text = await this.getBillingCsv(this.billingPeriod, this.billingGroupBy, this.billingIncludeTest)
        const blob = new Blob([typeof text === 'string' ? text : JSON.stringify(text)], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `app-billing-${this.billingPeriod}-by-${this.billingGroupBy}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.error = `Could not download the CSV: ${error}`
      }
    },
    modeName(code) {
      return MODES[code] || MODES.subscription
    },
    modeBadge(code) {
      return MODE_BADGES[code] || MODE_BADGES.subscription
    },
    stateName(code) {
      return STATES[code] || STATES.active
    },
    stateBadge(code) {
      return STATE_BADGES[code] || STATE_BADGES.active
    },
    refusalName(code) {
      return REFUSALS[code] || code
    },
    plural(count, word) {
      return `${count} ${word}${count === 1 ? '' : 's'}`
    },
    rowCreditsLeft(row) {
      return (row.planRemaining || 0) + (row.topUpRemaining || 0)
    },
    formatDay(value) {
      return value ? moment(value).format('MM/DD/YYYY') : ''
    },
    async submitCredits() {
      this.saved = null
      this.draftError = null
      const draft = this.creditDraft
      const body =
        draft.kind === 'top-up'
          ? { kind: 'top-up', count: draft.count, note: draft.note }
          : { kind: draft.kind, credits: draft.credits, note: draft.note }
      const id = this.selected.user.user
      const who = this.selected.user.name || this.selected.user.username
      try {
        await this.addCredits(id, body)
        if (this.showing(id)) {
          const left = this.isSubscription ? ` ${this.credits(this.creditsLeft)} credits left.` : ''
          this.saved =
            draft.kind === 'top-up'
              ? `Recorded ${draft.count} top-off${draft.count === 1 ? '' : 's'}.${left}`
              : `${draft.kind === 'grant' ? 'Added' : 'Corrected by'} ${this.credits(draft.credits)} credits.${left}`
          this.resetDrafts()
        }
        await this.refreshAfterSave()
      } catch (error) {
        this.saveFailed(id, who, error)
      }
    },
    functionName(code) {
      return FUNCTIONS[code] || code
    },
    reasonName(code) {
      return REASONS[code] || code
    },
    purchaseKind(code) {
      return PURCHASES[code] || code
    },
    credits(value) {
      if (value == null) return ''
      const n = Number(value)
      return Number.isInteger(n) ? String(n) : n.toFixed(2)
    },
    usd(value) {
      return value == null ? '' : `$${Number(value).toFixed(2)}`
    },
    // A daily maximum of 0 is no limit (App pricing's "No limit").
    dailyMaxText(value) {
      return Number(value) > 0 ? `${this.usd(value)} a day (UTC)` : 'no limit'
    },
    monthName(period) {
      return /^\d{4}-\d{2}$/.test(period || '') ? moment(`${period}-01`).format('MMMM YYYY') : period || ''
    },
    formatDate(value) {
      return value ? moment(value).format('MM/DD/YYYY h:mm A') : ''
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-spinner {
  text-align: center;
  padding: 2rem;
}
.custom-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.custom-row {
  cursor: pointer;
}
.custom-tile {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}
.custom-tile-value {
  font-size: 1.5rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.custom-panel {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  height: 100%;
}
</style>
